import type { ReactNode } from 'react';
import { cssVars, cx } from '@/lib/cx';
import { formatDate, formatDuration, getDisplayTimeZone, toDate } from '@/lib/format';
import { t } from '@/lib/i18n';
import { dayOf, zonedMidnight } from '@/lib/timeRange';
import s from './Timeline.module.css';
import { Tooltip } from './Tooltip';

export type TimelineTone = 'primary' | 'good' | 'medium' | 'bad' | 'neutral' | 'accent';

export interface TimelineSegment {
  id: string;
  start: Date | string;
  end: Date | string;
  tone?: TimelineTone;
  label?: string;
  /** Hatched: e.g. video that can be requested but is not uploaded yet. */
  pattern?: boolean;
  onSelect?: () => void;
}

export interface TimelineMarker {
  id: string;
  at: Date | string;
  tone?: TimelineTone;
  label?: string;
  onSelect?: () => void;
}

export interface TimelineLane {
  id: string;
  label: ReactNode;
  segments?: TimelineSegment[];
  markers?: TimelineMarker[];
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

function ticksFor(from: number, to: number, tz: string): { at: number; label: string; major: boolean }[] {
  const span = to - from;
  const out: { at: number; label: string; major: boolean }[] = [];
  if (span <= 2 * DAY) {
    // Short windows (a zoomed view of minutes) get minute ticks; a day gets hours.
    const MIN = 60_000;
    const step =
      span <= 30 * MIN
        ? 5 * MIN
        : span <= HOUR
          ? 10 * MIN
          : span <= 3 * HOUR
            ? 30 * MIN
            : (span <= 6 * HOUR ? 1 : span <= 12 * HOUR ? 2 : span <= DAY ? 3 : 6) * HOUR;
    let at = zonedMidnight(dayOf(new Date(from), tz), tz).getTime();
    at += Math.max(0, Math.floor((from - at) / step)) * step;
    while (at < to) {
      if (at >= from) {
        const midnight = zonedMidnight(dayOf(new Date(at), tz), tz).getTime() === at;
        out.push({ at, label: midnight ? formatDate(at, 'short', tz) : formatDate(at, 'time', tz), major: midnight });
      }
      at += step;
    }
    return out;
  }
  const stepDays = span <= 16 * DAY ? 1 : span <= 45 * DAY ? 7 : 14;
  let day = dayOf(new Date(from), tz);
  for (let i = 0; i < 400; i++) {
    const at = zonedMidnight(day, tz).getTime();
    if (at >= to) break;
    if (at >= from) out.push({ at, label: formatDate(at, 'short', tz), major: true });
    const next = new Date(Date.UTC(day.y, day.m - 1, day.d + stepDays));
    day = { y: next.getUTCFullYear(), m: next.getUTCMonth() + 1, d: next.getUTCDate() };
  }
  return out;
}

/**
 * The single timeline component (RULES §UI-2): lanes of segments and markers on a real time
 * axis — activity, video availability, tachograph activities, dispatch. Times are shown in
 * the company timezone.
 */
export function Timeline({
  start,
  end,
  lanes,
  now,
  label,
  className,
  highlight,
  onPick,
}: {
  start: Date | string;
  end: Date | string;
  lanes: TimelineLane[];
  now?: Date;
  label: string;
  className?: string;
  /** A span shaded across every lane (e.g. the clip being requested). */
  highlight?: { start: Date | string; end: Date | string };
  /** Clicking a lane's track picks that instant (pointer convenience; offer inputs too). */
  onPick?: (at: Date, laneId: string) => void;
}) {
  const tz = getDisplayTimeZone();
  const from = toDate(start)?.getTime() ?? 0;
  const to = toDate(end)?.getTime() ?? from + DAY;
  const span = Math.max(1, to - from);
  const pos = (v: Date | string) => ((toDate(v)?.getTime() ?? from) - from) / span;
  const pct = (x: number) => `${Math.max(0, Math.min(1, x)) * 100}%`;
  const ticks = ticksFor(from, to, tz);
  const nowX = now ? pos(now) : null;

  return (
    <div className={cx(s.timeline, className)} role="figure" aria-label={label}>
      <div className={s.axisRow}>
        <div className={s.laneLabel} />
        <div className={s.axis} aria-hidden>
          {ticks.map((tk) => (
            <span key={tk.at} className={cx(s.tick, tk.major && s.major)} style={cssVars({ '--x': pct(pos(new Date(tk.at))) })}>
              {tk.label}
            </span>
          ))}
        </div>
      </div>
      <div className={s.lanes}>
        <div className={s.grid} aria-hidden>
          {ticks.map((tk) => (
            <span key={tk.at} className={cx(s.gridline, tk.major && s.major)} style={cssVars({ '--x': pct(pos(new Date(tk.at))) })} />
          ))}
          {highlight && (
            <span
              className={s.highlight}
              style={cssVars({ '--x': pct(pos(highlight.start)), '--w': pct(Math.min(1, pos(highlight.end)) - Math.max(0, pos(highlight.start))) })}
            />
          )}
          {nowX != null && nowX >= 0 && nowX <= 1 && <span className={s.now} style={cssVars({ '--x': pct(nowX) })} title={t('Now')} />}
        </div>
        {lanes.map((lane) => (
          <div key={lane.id} className={s.lane}>
            <div className={s.laneLabel}>{lane.label}</div>
            <div
              className={cx(s.track, onPick && s.pickable)}
              onClick={
                onPick
                  ? (e) => {
                      const box = e.currentTarget.getBoundingClientRect();
                      const x = Math.max(0, Math.min(1, (e.clientX - box.left) / Math.max(1, box.width)));
                      onPick(new Date(from + x * span), lane.id);
                    }
                  : undefined
              }
            >
              {lane.segments?.map((seg) => {
                const x0 = pos(seg.start);
                const x1 = pos(seg.end);
                if (x1 < 0 || x0 > 1) return null;
                const dur = ((toDate(seg.end)?.getTime() ?? 0) - (toDate(seg.start)?.getTime() ?? 0)) / 1000;
                const tip = `${seg.label ? `${seg.label} · ` : ''}${formatDate(seg.start, 'time', tz)}–${formatDate(seg.end, 'time', tz)} (${formatDuration(dur)})`;
                return (
                  <Tooltip key={seg.id} content={tip}>
                    <button
                      type="button"
                      className={cx(s.segment, s[seg.tone ?? 'primary'], seg.pattern && s.pattern)}
                      style={cssVars({ '--x': pct(x0), '--w': pct(Math.min(1, x1) - Math.max(0, x0)) })}
                      onClick={
                        seg.onSelect
                          ? (e) => {
                              e.stopPropagation(); // a selectable block is not a pick on the track
                              seg.onSelect?.();
                            }
                          : undefined
                      }
                      aria-label={tip}
                    />
                  </Tooltip>
                );
              })}
              {lane.markers?.map((m) => {
                const x = pos(m.at);
                if (x < 0 || x > 1) return null;
                const tip = `${m.label ? `${m.label} · ` : ''}${formatDate(m.at, 'time', tz)}`;
                return (
                  <Tooltip key={m.id} content={tip}>
                    <button
                      type="button"
                      className={cx(s.marker, s[m.tone ?? 'bad'])}
                      style={cssVars({ '--x': pct(x) })}
                      onClick={
                        m.onSelect
                          ? (e) => {
                              e.stopPropagation();
                              m.onSelect?.();
                            }
                          : undefined
                      }
                      aria-label={tip}
                    />
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
